import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { Button } from '../../components/Button';
import { Block } from './Blocks';
import { chaptersFor } from './content';
import type { HandbookAudience, HandbookChapter } from './model';
import { searchHandbook } from './search';

/**
 * Das Handbuch.
 *
 * **Warum es in der Anwendung steht und nicht im Netzlaufwerk.** Eine PDF
 * im Ordner "Dokumentation" ist nach dem zweiten Feature falsch und wird
 * trotzdem weiter gelesen. Ein Kapitel neben der Maske, die es beschreibt,
 * faellt auf, wenn es nicht mehr stimmt -- und der Sprung aus dem Kapitel in
 * die Maske macht aus dem Nachschlagen einen Arbeitsschritt statt einer
 * Unterbrechung.
 *
 * **Warum jeder alles sieht.** Ein Bearbeiter, der wissen will, warum ein
 * Status die SLA-Uhr anhaelt, soll das nachlesen koennen, auch wenn er den
 * Haken nicht selbst setzen darf. Die Kennzeichnung je Kapitel sagt, wer es
 * braucht; der Filter darueber grenzt ein, wer mag. Verborgen wird nichts --
 * das Handbuch enthaelt keine Daten, sondern Beschreibungen, und was der
 * Server nicht herausgibt, gibt er auch nach dem Lesen nicht heraus.
 */

const AUDIENCE_TONE: Record<HandbookAudience, BadgeTone> = {
  agent: 'info',
  admin: 'warning',
  privacy: 'success',
};

const AUDIENCES: HandbookAudience[] = ['agent', 'admin', 'privacy'];

export function HandbookPage({ chapterId }: { chapterId?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [audience, setAudience] = useState<HandbookAudience | ''>('');

  const chapters = useMemo(() => chaptersFor(i18n.language), [i18n.language]);

  const hits = useMemo(() => {
    const found = searchHandbook(chapters, search);
    return audience ? found.filter((hit) => hit.chapter.audience === audience) : found;
  }, [chapters, search, audience]);

  // Die Adresse gewinnt gegen alles andere: Ein Kollege schickt einen Verweis
  // auf ein Kapitel, und dieses Kapitel soll aufgehen -- auch wenn der Filter
  // es aus der Liste nimmt.
  const current: HandbookChapter | undefined =
    chapters.find((chapter) => chapter.id === chapterId) ?? hits[0]?.chapter ?? chapters[0];

  // Bei aktiver Suche nur die Abschnitte zeigen, die den Begriff enthalten.
  // Wer "Feiertag" sucht, will nicht das ganze SLA-Kapitel durchsehen.
  const matchedSections = hits.find((hit) => hit.chapter.id === current?.id)?.sections ?? [];
  const filtering = search.trim().length > 0 && matchedSections.length > 0;
  const sections = filtering ? matchedSections : (current?.sections ?? []);

  return (
    <>
      <PageHeader title={t('handbook.title')} subtitle={t('handbook.subtitle')} />

      <div className="handbook">
        <nav className="handbook__index" aria-label={t('handbook.chapters')}>
          <label className="field">
            <span className="field__label">{t('handbook.searchLabel')}</span>
            <input
              type="search"
              className="field__control"
              value={search}
              placeholder={t('handbook.searchPlaceholder')}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </label>

          <div className="handbook__filter" role="group" aria-label={t('handbook.audience')}>
            <Button
              variant={audience === '' ? 'primary' : 'ghost'}
              onClick={() => {
                setAudience('');
              }}
            >
              {t('handbook.audienceAll')}
            </Button>
            {AUDIENCES.map((key) => (
              <Button
                key={key}
                variant={audience === key ? 'primary' : 'ghost'}
                onClick={() => {
                  setAudience(key);
                }}
              >
                {t(`handbook.audienceName.${key}`)}
              </Button>
            ))}
          </div>

          {hits.length === 0 ? (
            <EmptyState
              icon="search"
              title={t('handbook.noHits')}
              hint={t('handbook.noHitsHint')}
            />
          ) : (
            <ol className="handbook__chapters">
              {hits.map(({ chapter }) => (
                <li key={chapter.id}>
                  <Link
                    to="/app/handbuch/$chapterId"
                    params={{ chapterId: chapter.id }}
                    className="handbook__chapter-link"
                    aria-current={chapter.id === current?.id ? 'page' : undefined}
                  >
                    <span className="handbook__chapter-title">{chapter.title}</span>
                    <span className="handbook__chapter-summary">{chapter.summary}</span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </nav>

        <article className="handbook__body card">
          {current ? (
            <>
              <header className="handbook__chapter-head">
                <Badge tone={AUDIENCE_TONE[current.audience]}>
                  {t(`handbook.audienceName.${current.audience}`)}
                </Badge>
                <h2>{current.title}</h2>
                <p className="table__muted">{current.summary}</p>
                {current.route ? (
                  <p className="handbook__route">
                    <Link to={current.route} className="handbook__route-link">
                      {t('handbook.openMask')}
                      <span aria-hidden="true"> →</span>
                    </Link>
                  </p>
                ) : null}
              </header>

              {filtering ? (
                <p className="field__hint" role="status">
                  {t('handbook.filteredSections', { count: sections.length })}{' '}
                  <button
                    type="button"
                    className="handbook__reset"
                    onClick={() => {
                      setSearch('');
                    }}
                  >
                    {t('handbook.showWholeChapter')}
                  </button>
                </p>
              ) : sections.length > 1 ? (
                <nav className="handbook__toc" aria-label={t('handbook.onThisPage')}>
                  <p className="handbook__toc-title">{t('handbook.onThisPage')}</p>
                  <ul>
                    {sections.map((section) => (
                      <li key={section.id}>
                        <a href={`#${current.id}-${section.id}`}>{section.title}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
              ) : null}

              {sections.map((section) => (
                <section
                  key={section.id}
                  id={`${current.id}-${section.id}`}
                  className="handbook__section"
                >
                  <h3>{section.title}</h3>
                  {section.blocks.map((block, index) => (
                    <Block key={index} block={block} />
                  ))}
                </section>
              ))}

              <footer className="handbook__foot">
                <ChapterStep
                  chapters={chapters}
                  currentId={current.id}
                  direction={-1}
                  label={t('handbook.previous')}
                />
                <ChapterStep
                  chapters={chapters}
                  currentId={current.id}
                  direction={1}
                  label={t('handbook.next')}
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    void navigate({ to: '/app/handbuch' });
                    setSearch('');
                    setAudience('');
                  }}
                >
                  {t('handbook.toStart')}
                </Button>
              </footer>
            </>
          ) : null}
        </article>
      </div>
    </>
  );
}

/**
 * Ein Schritt zum vorigen oder naechsten Kapitel.
 *
 * Die Reihenfolge in ``content.ts`` ist die Leseordnung -- diese beiden
 * Knoepfe machen sie benutzbar. Ohne sie waere das Handbuch ein
 * Nachschlagewerk und nichts sonst; mit ihnen laesst es sich auch einmal von
 * vorn durchgehen, etwa bei der Einarbeitung.
 */
function ChapterStep({
  chapters,
  currentId,
  direction,
  label,
}: {
  chapters: HandbookChapter[];
  currentId: string;
  direction: 1 | -1;
  label: string;
}) {
  const index = chapters.findIndex((chapter) => chapter.id === currentId);
  const target = index < 0 ? undefined : chapters[index + direction];

  if (!target) return null;

  return (
    <Link
      to="/app/handbuch/$chapterId"
      params={{ chapterId: target.id }}
      className="handbook__step-link"
    >
      {direction < 0 ? <span aria-hidden="true">← </span> : null}
      {label}: {target.title}
      {direction > 0 ? <span aria-hidden="true"> →</span> : null}
    </Link>
  );
}
